/**
 * Authentication Service
 * Handles user authentication and session management
 * 
 * Uses TriSys Web API for actual authentication.
 * See specifications/TriSys-Web-API-Integration.md for API details.
 */

import { Logger } from './Logger';
import { trisysAPI, UserProfile, SessionExpiredError } from './api/TriSysWebAPI';
import { tenantService } from './TenantService';
import { bsgProfileService } from './BSGProfileService';
import { ToasterService } from './ToasterService';
import { ModalSystem } from './ModalSystem';

const logger = Logger.scope('AuthService');

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthUser {
  id?: string;
  contactId?: number;
  email: string;
  name: string;
  avatar?: string;
  jobTitle?: string;
  companyName?: string;
}

export interface AuthenticationResult {
  success: boolean;
  message?: string;
  user?: AuthUser;
  credentialsCorrectButBlocked?: boolean;
}

// Re-export for convenience
export { SessionExpiredError } from './api/TriSysWebAPI';

class AuthenticationService {
  private currentUser: AuthUser | null = null;
  private onSessionExpiredCallbacks: Array<() => void> = [];

  constructor() {
    // Check if we have a cached session on startup
    if (trisysAPI.isAuthenticated()) {
      logger.debug('Found existing session');
    }
  }

  /**
   * Register a callback to be called when session expires
   */
  public onSessionExpired(callback: () => void): void {
    this.onSessionExpiredCallbacks.push(callback);
  }

  /**
   * Notify all registered callbacks that session has expired
   */
  private notifySessionExpired(): void {
    for (const callback of this.onSessionExpiredCallbacks) {
      try {
        callback();
      } catch (error) {
        logger.error('Session expired callback error:', error);
      }
    }
  }

  /**
   * Authenticate user with email and password
   * @param credentials User login credentials
   * @returns Authentication result
   */
  public async login(credentials: LoginCredentials): Promise<AuthenticationResult> {
    logger.debug('Login attempt for:', credentials.email);

    // Validate credentials locally first
    if (!credentials.email || !credentials.password) {
      return {
        success: false,
        message: 'Email and password are required'
      };
    }

    if (!credentials.email.includes('@')) {
      return {
        success: false,
        message: 'Invalid email format'
      };
    }

    try {
      // Call TriSys Web API
      // BSG API Hub uses AccountType 12 (TriSysCRMCredentialAccountTypeEnum.NonCustomer)
      const accountType = tenantService.getTenantId() === 'bsg-api-hub' ? 12 : undefined;
      const response = await trisysAPI.loginWithCredentials(
        credentials.email,
        credentials.password,
        accountType
      );

      if (!response.Success) {
        return {
          success: false,
          message: response.ErrorMessage || 'Login failed',
          credentialsCorrectButBlocked: response.CredentialsCorrectButSomethingElsePreventsLogin
        };
      }

      // Load user profile after successful login
      const profile = await trisysAPI.getUserProfile();
      
      // Load standing data (reference data for lookups and CRUD)
      // CRITICAL: Must await this - forms depend on FieldDescriptions for CRUD operations
      try {
        const standingData = await trisysAPI.getStandingData();
        if (standingData) {
          logger.debug('Standing data cached after login');
        } else {
          logger.warn('Standing data returned null after login');
        }
      } catch (err) {
        logger.warn('Failed to cache standing data:', err);
      }
      
      logger.info('Profile loaded from API:', profile);
      
      if (profile) {
        this.currentUser = this.profileToAuthUser(profile);
        // Ensure email is set (profile might have empty email if contact is missing)
        if (!this.currentUser.email) {
          this.currentUser.email = credentials.email;
        }

        // Load BSG API Hub profile if this is the bsg-api-hub tenant
        const bsgOk = await this.loadBSGProfileIfApplicable(profile.contactId);
        if (!bsgOk) {
          return { success: false, message: 'Access denied for BSG API Hub' };
        }
      } else {
        // Fallback if profile load fails
        this.currentUser = {
          email: credentials.email,
          name: credentials.email.split('@')[0]
        };
      }

      logger.info('Login successful, currentUser:', this.currentUser);

      return {
        success: true,
        message: 'Login successful',
        user: this.currentUser
      };
    } catch (error) {
      logger.error('Login error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed'
      };
    }
  }

  /**
   * Login with Auth0 social authentication
   */
  public async loginWithAuth0(email: string, auth0User: object): Promise<AuthenticationResult> {
    logger.debug('Auth0 login for:', email);

    try {
      const response = await trisysAPI.loginWithAuth0(email, auth0User,
        tenantService.getTenantId() === 'bsg-api-hub' ? 12 : undefined
      );

      if (!response.Success) {
        return {
          success: false,
          message: response.ErrorMessage || 'Social login failed',
          credentialsCorrectButBlocked: response.CredentialsCorrectButSomethingElsePreventsLogin
        };
      }

      // Load user profile
      const profile = await trisysAPI.getUserProfile();
      const socialUser = auth0User as any;
      
      // Load standing data (reference data for lookups and CRUD)
      // CRITICAL: Must await this - forms depend on FieldDescriptions for CRUD operations
      try {
        const standingData = await trisysAPI.getStandingData();
        if (standingData) {
          logger.debug('Standing data cached after Auth0 login');
        } else {
          logger.warn('Standing data returned null after Auth0 login');
        }
      } catch (err) {
        logger.warn('Failed to cache standing data after Auth0 login:', err);
      }
      
      if (profile) {
        this.currentUser = this.profileToAuthUser(profile);
        
        // Ensure email is set
        if (!this.currentUser.email) {
          this.currentUser.email = email;
        }

        // Load BSG API Hub profile if this is the bsg-api-hub tenant
        const bsgOk = await this.loadBSGProfileIfApplicable(profile.contactId);
        if (!bsgOk) {
          return { success: false, message: 'Access denied for BSG API Hub' };
        }

        // Augment with social data if missing in CRM profile
        if (!this.currentUser.avatar && socialUser.picture) {
          this.currentUser.avatar = socialUser.picture;
          logger.info('Using social avatar as fallback');
        }
        
        // If display name is just the email or login name, prefer social name
        if (socialUser.name && (
            !this.currentUser.name || 
            this.currentUser.name.includes('@') || 
            this.currentUser.name === profile.loginName
        )) {
           this.currentUser.name = socialUser.name;
           logger.info('Using social name as fallback');
        }
      } else {
        this.currentUser = {
          email,
          name: socialUser.name || email.split('@')[0],
          avatar: socialUser.picture
        };
        logger.warn('Profile load failed, using social details fallback');
      }

      logger.debug('Auth0 login successful for:', this.currentUser.name);

      return {
        success: true,
        message: 'Login successful',
        user: this.currentUser
      };
    } catch (error) {
      logger.error('Auth0 login error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Social login failed'
      };
    }
  }

  /**
   * Convert UserProfile to AuthUser
   */
  private profileToAuthUser(profile: UserProfile): AuthUser {
    logger.info('profileToAuthUser received:', {
      displayName: profile.displayName,
      jobTitle: profile.jobTitle,
      companyName: profile.companyName,
      avatar: profile.avatar,
      email: profile.email
    });
    
    const authUser: AuthUser = {
      id: String(profile.userId),
      contactId: profile.contactId,
      email: profile.email,
      name: profile.displayName,
      avatar: profile.avatar || undefined,
      jobTitle: profile.jobTitle,
      companyName: profile.companyName
    };
    
    logger.info('profileToAuthUser returning:', authUser);
    return authUser;
  }

  /**
   * Load BSG API Hub profile if the current tenant is bsg-api-hub.
   * Returns false if the user is not authorised (caller must abort login).
   */
  private async loadBSGProfileIfApplicable(contactId: number): Promise<boolean> {
    if (tenantService.getTenantId() !== 'bsg-api-hub') return true;

    try {
      logger.debug('Loading BSG API Hub profile', { contactId });
      const result = await bsgProfileService.load(contactId);

      if (!result || !result.Success) {
        const errorMsg = result?.ErrorMessage || 'You are not authorised for BSG API Hub.';
        logger.warn('BSG API Hub access denied', { error: errorMsg });
        ModalSystem.show({
          title: 'Access Denied',
          html: `<p>${errorMsg}</p>`,
          width: 'md',
          buttons: [{ caption: 'OK', className: 'btn-primary', callback: () => true }]
        });
        await trisysAPI.logout();
        this.currentUser = null;
        return false;
      }

      // Override AuthUser with BSG profile data (authoritative source for BSG API Hub)
      if (this.currentUser) {
        if (result.FullName) this.currentUser.name = result.FullName;
        if (result.CompanyName) this.currentUser.companyName = result.CompanyName;
        if (result.PhotoURL) this.currentUser.avatar = result.PhotoURL;
        if (result.WorkEMailAddress) this.currentUser.email = result.WorkEMailAddress;
        logger.debug('AuthUser overridden with BSG profile data', {
          name: this.currentUser.name,
          companyName: this.currentUser.companyName,
          avatar: this.currentUser.avatar,
          email: this.currentUser.email
        });
      }

      return true;
    } catch (err) {
      logger.warn('Failed to load BSG API Hub profile:', err);
      return true; // Don't block login for network errors
    }
  }

  /**
   * Verify the current session is still valid
   * Also loads the user profile if session is valid (for returning users)
   */
  public async verifySession(): Promise<boolean> {
    const isValid = await trisysAPI.verifySession();
    
    if (!isValid) {
      this.currentUser = null;
      this.notifySessionExpired();
      return false;
    }

    // Session is valid - load user profile if not already cached
    if (!this.currentUser) {
      logger.debug('Session valid, loading user profile...');
      const profile = await trisysAPI.getUserProfile();
      if (profile) {
        this.currentUser = this.profileToAuthUser(profile);
        logger.debug('User profile loaded for returning user:', this.currentUser.name);

        // Load BSG API Hub profile if this is the bsg-api-hub tenant
        const bsgOk = await this.loadBSGProfileIfApplicable(profile.contactId);
        if (!bsgOk) {
          return false; // Session is valid but user not authorised — bounce to login
        }
      }
    }

    return isValid;
  }

  /**
   * Logout current user
   */
  public async logout(): Promise<void> {
    logger.debug('Logging out user:', this.currentUser?.email);
    
    await trisysAPI.logout();
    this.currentUser = null;
    
    logger.debug('Logout complete');
  }

  /**
   * Check if user is currently authenticated
   */
  public isAuthenticated(): boolean {
    return trisysAPI.isAuthenticated();
  }

  /**
   * Get current authenticated user
   */
  public getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Get the full user profile from TriSys API
   */
  public async getUserProfile(): Promise<UserProfile | null> {
    return trisysAPI.getUserProfile();
  }

  /**
   * Handle session expired error
   * Call this when any API call fails with SessionExpiredError
   */
  public handleSessionExpired(): void {
    logger.warn('Session expired, clearing authentication');
    trisysAPI.clearSession();
    this.currentUser = null;
    this.notifySessionExpired();
  }
}

// Export singleton instance
export const authService = new AuthenticationService();
